import React, { useState, useEffect, useContext, useRef, useCallback, createContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { pusherContext } from 'services/Pusher'
import { apiGetMyMentions } from 'services/TicketsService'
import { setHaveNotifications } from 'store/auth/userSlice'
import { cleanIsoDate } from 'utils/storeHube/helpersDate'
import { referenciaDelHallazgo } from 'utils/storeHube/tituloDelHallazgo'
import { claveDeGrupo, fusionarEntrePestanas, mismasNotificaciones, yaEstaEnLaLista } from './notificationHelpers'
import { useTranslation } from "react-i18next";
import NotificationToaster from './NotificationToaster'
import NotificationUndoBar from './NotificationUndoBar'

// Cuánto dura el "Deshacer" de un borrado. Seis segundos y no uno: el fallo
// documentado de este patrón es una ventana tan corta que el usuario ve el
// cartel pero no llega a usarlo.
const MS_DESHACER = 6000

// Clave estable de una notificación de mención, para que la que llega por Pusher
// y la que trae el GET al abrir la app sean la MISMA y no se dupliquen.
// El id de la fila de mención no sirve: el evento de Pusher no lo manda.
const mentionKey = (ticketReplyId) => `mention-${ticketReplyId}`

// Estado local de las menciones, aparte de la lista.
//
// La lista de menciones la reconstruye el servidor en cada carga, así que lo que
// el usuario hizo con ellas (leerlas, limpiarlas) no puede vivir dentro de la
// lista: se perdería en el próximo merge y "Limpiar todo" no serviría de nada
// para menciones, volverían enteras al recargar.
//
// Vive en el navegador a propósito, igual que antes: es información de baja
// consecuencia. Si se pierde, lo peor que pasa es un punto sin leer de más.
// La identidad de las dos keys es el CANAL (`storarmega.<uuid>`), no el uuid: el
// uuid se repite entre tenants, así que la misma persona en dos tenants
// compartía el cache del navegador y veía ahí los hallazgos del otro.
const estadoStorageKey = (identidad) => `storehub_notification_state_${identidad}`
const listaStorageKey = (identidad) => `storehub_notifications_${identidad}`

// Cache viejo, indexado sólo por uuid. Se borra en vez de migrarse: las
// notificaciones guardadas no tienen tenant adentro, así que no hay forma de
// separar las propias de las que se colaron de otro tenant. Lo que se pierde es
// el "ya la vi" de las menciones; el contenido lo repone el servidor.
const purgarCacheDeUuidPelado = (uuid) => {
  if (!uuid) return
  try {
    localStorage.removeItem(`storehub_notifications_${uuid}`)
    localStorage.removeItem(`storehub_notification_state_${uuid}`)
  } catch (error) {
    // Un navegador sin localStorage no tiene nada que purgar.
  }
}

// Tope de keys recordadas. Sin esto el objeto crece sin límite en el navegador de
// alguien que recibe menciones todos los días durante años.
const MAX_ESTADO = 200

const leerEstado = (identidad) => {
  try {
    const crudo = localStorage.getItem(estadoStorageKey(identidad))
    if (!crudo) return {}
    const parsed = JSON.parse(crudo)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch (error) {
    return {}
  }
}

const guardarEstado = (identidad, estado) => {
  try {
    const keys = Object.keys(estado)
    // Object.keys respeta el orden de inserción, así que las que se caen son las
    // que hace más tiempo que no se tocan. Esa promesa sólo se cumple si las
    // que se tocan se reinsertan: ver tocarEstado.
    const acotado = keys.length > MAX_ESTADO
      ? Object.fromEntries(keys.slice(keys.length - MAX_ESTADO).map(k => [k, estado[k]]))
      : estado

    localStorage.setItem(estadoStorageKey(identidad), JSON.stringify(acotado))
  } catch (error) {
    console.error('❌ Error guardando el estado de las menciones:', error)
  }
}

// Marca una key del estado, moviéndola al final del objeto.
//
// El `delete` no es decorativo: reasignar una propiedad que YA existe no cambia
// su posición en el orden de inserción de JS. Sin él, marcar como borrada una
// mención vieja la dejaba donde estaba, y el recorte de MAX_ESTADO la expulsaba
// enseguida: la mención volvía a aparecer en el próximo merge como si nunca se
// hubiera borrado. Justo las que más importa recordar son las que se acaban de
// tocar.
const tocarEstado = (estado, key, cambio) => {
  const previo = estado[key]
  delete estado[key]
  estado[key] = { ...previo, ...cambio }
  return estado
}

// Mezcla lo que hay en pantalla con lo que manda el servidor.
//
// Para las menciones, el servidor es la AUTORIDAD: si dejó de devolver una, es
// porque el usuario ya no tiene alcance a esa tienda o el hallazgo se borró, así
// que no puede seguir viéndose en la campanita con su autor y su extracto.
//
// La excepción es lo que llegó por Pusher mientras volaba el GET: eso es más
// nuevo que la consulta y todavía no podía estar en la respuesta. Se distingue
// por `keysDelCache`, que son exactamente las menciones que se habían leído del
// navegador antes de disparar el fetch. NO se compara por timestamp: el del
// navegador y el `created_at` del servidor salen de relojes distintos, y con un
// cliente desfasado una mención revocada podría sobrevivir al corte.
//
// El "ya la vi" y el "ya la limpié" salen de `estado`, no de la lista, porque la
// lista se reconstruye entera en cada carga.
//
// Los otros 5 tipos de aviso no se tocan: siguen viviendo sólo en el navegador.
//
// Fuera del componente y con `t` por parámetro: es una función pura, no depende
// del orden de declaración dentro del cuerpo del componente y se puede testear.
export const mergeMenciones = (locales, delServidor, t, opciones = {}) => {
  const { keysDelCache = new Set(), estado = {} } = opciones

  const previas = locales || []

  // Los otros 5 tipos se conservan tal cual y NO pasan por el Map: usan Date.now()
  // como id, y dos avisos del mismo milisegundo colisionarían en la misma clave,
  // así que traer las menciones del servidor podía hacer desaparecer un aviso que
  // no tiene nada que ver.
  const otrosTipos = previas.filter(n => n.type !== 'mention')

  // Menciones que entraron en vivo mientras volaba el GET: no estaban en el cache,
  // así que el servidor no podía conocerlas. Sin key es del formato viejo, o sea
  // del cache.
  const enVivo = previas.filter(
    n => n.type === 'mention' && n.key && !keysDelCache.has(n.key)
  )

  const porKey = new Map(enVivo.map(n => [n.key, n]))

  ;(delServidor || []).forEach((m) => {
    const key = mentionKey(m.ticket_reply_id)

    // El usuario ya la limpió de la campanita. El servidor la sigue teniendo
    // porque la mención no se borra nunca, pero no hay que volver a mostrarla.
    if (estado[key]?.dismissed) {
      porKey.delete(key)
      return
    }

    const previa = porKey.get(key)
    const autor = m.mentioned_by ? `${m.mentioned_by} ` : ''

    porKey.set(key, {
      id: previa?.id ?? key,
      key,
      type: 'mention',
      title: `${autor}${t('mentioned_you_in', 'te mencionó en')} ${t('ticket_alias')} #${m.ticket_id}`,
      message: m.message || '',
      // cleanIsoDate y no `new Date(created_at)` pelado: el backend manda el valor
      // ya expresado en la tz del tenant pero lo etiqueta con 'Z' (ver helpersDate).
      // Sin sacar esa marca, el navegador cree que es UTC y resta el offset: un
      // comentario de las 10:18 aparecia como 7:18 en la campanita.
      timestamp: new Date(cleanIsoDate(m.created_at)),
      // Autor aparte del título, que ya lo trae concatenado: la fila lo necesita
      // suelto para el avatar y para poder cortar el nombre con puntos
      // suspensivos sin romper el resto de la frase.
      author: { name: m.mentioned_by || '', image: m.mentioned_by_image || null },
      read: estado[key]?.read ?? previa?.read ?? false,
      // El replyId es lo que permite bajar hasta el comentario exacto: en un
      // hallazgo con decenas de comentarios, abrirlo arriba de todo no dice cuál
      // de todos te mencionó.
      data: { ticketId: m.ticket_id, replyId: m.ticket_reply_id },
    })
  })

  return [...otrosTipos, ...porKey.values()].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  )
}

// Contexto para compartir notificaciones con otros componentes
// Los valores por defecto no son decorativos: mientras el usuario no está
// autenticado, o mientras la lista todavía es la del usuario anterior, el
// provider no se monta y los consumidores usan esto. Sin las acciones nuevas
// acá, la campanita reventaría al tocar un botón en ese frame.
export const NotificationContext = createContext({
  notifications: [],
  clearAll: () => {},
  handleNotificationClick: () => {},
  dismissOne: () => {},
  dismissMany: () => {},
  markAllRead: () => {},
  undo: () => {},
  pendingUndo: 0,
})

// Hook para usar el contexto de notificaciones
export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications debe usarse dentro de NotificationManager')
  }
  return context
}

const NotificationManager = ({ children }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const [notifications, setNotifications] = useState([])
  const { session, user } = useSelector((state) => state.auth)
  const { signedIn, token } = session
  const pusherChannel = useContext(pusherContext)

  // Avisos efímeros que se ven arriba a la derecha mientras la app está abierta.
  // Van aparte de `notifications`: son la misma información pero con otro ciclo
  // de vida, y cerrar uno no puede significar borrar la notificación.
  const [toasts, setToasts] = useState([])

  // Borrado pendiente de confirmar. El borrado YA se aplicó (la marca está
  // escrita en el navegador): esto sólo guarda con qué reponerlo si el usuario
  // toca Deshacer, así que cerrar la pestaña antes de los seis segundos deja el
  // borrado hecho, que es lo que la persona pidió.
  const [deshacer, setDeshacer] = useState(null)
  const timerDeshacer = useRef(null)

  // Los listeners de Pusher se registran una sola vez y capturan el render en el
  // que se crearon. `notifications` no se ve afectado porque siempre se actualiza
  // con la forma funcional del setter, pero la ruta actual sí: sin el ref, el
  // listener creería para siempre que estamos en la pantalla donde estábamos al
  // conectarnos, y la regla de "no avisar de lo que ya estás mirando" decidiría
  // sobre una URL vieja.
  const locationRef = useRef(location)
  useEffect(() => { locationRef.current = location }, [location])

  // Misma razón que el ref de la ruta: los listeners necesitan preguntar por la
  // lista de ahora, no por la del render en que se registraron. Los setters
  // siguen usando la forma funcional; esto es sólo para leer.
  const notificationsRef = useRef(notifications)
  useEffect(() => { notificationsRef.current = notifications }, [notifications])

  // Último recurso para el id de un aviso: sólo se usa si el evento llegó sin
  // `event_id` (backend viejo). Dos eventos del mismo milisegundo compartirían
  // id, así que hace falta un desempate.
  const contadorDeId = useRef(0)

  // ============================================================================
  // PUSHER BEAMS - Ya se inicializa en src/services/Pusher/index.js
  // NO inicializar aquí para evitar duplicación
  // ============================================================================
  // NOTA: Pusher Beams se inicializa automáticamente cuando el usuario se autentica
  // Ver: src/services/Pusher/index.js líneas 56-86

  // Con qué nombre se guarda el cache de esta persona: su canal, o sea el uuid
  // con el tenant adelante. Es lo mismo que decide a qué canal de Pusher se
  // suscribe, así que el cache y la suscripción no se pueden desalinear.
  const identidad = user?.notificationsChannel

  // Identidad cuyo cache ya se leyó. Hasta entonces no se persiste nada.
  //
  // Guarda la identidad y no un booleano porque el efecto de guardado corre ANTES
  // de que se aplique la carga del usuario nuevo: con un booleano, al cambiar de
  // sesión escribía la lista del usuario anterior bajo la clave del nuevo.
  //
  // Es state y no ref, y se enciende junto con la lectura del cache (que es
  // sincrónica), NO cuando contesta el fetch. Si dependiera del fetch, un GET
  // lento o colgado dejaría de persistir todo lo que el usuario haga mientras
  // tanto: marcar leída, borrar, o cualquier aviso nuevo de los otros 5 tipos.
  // Un ref además no dispara render, así que el efecto de guardado ni se
  // enteraría de que ya puede escribir.
  const [hydratedFor, setHydratedFor] = useState(null)

  // Cuándo persistió esta pestaña por última vez. Es lo que le permite a la
  // fusión entre pestañas distinguir "esto lo acabo de recibir yo" de "esto lo
  // borró la otra". Ver `fusionarEntrePestanas`.
  const ultimaEscritura = useRef(0)

  // ============================================================================
  // CARGA INICIAL - localStorage (cache) + menciones del servidor (autoridad)
  // ============================================================================
  // Las menciones son el único tipo que sobrevive a no haber estado conectado:
  // vienen de la tabla ticket_reply_mention. Los otros 5 tipos siguen dependiendo
  // de haber recibido el evento de Pusher en vivo.
  //
  // El estado de "ya la vi" se sigue guardando en el navegador a propósito: si se
  // pierde, lo peor que pasa es un punto sin leer de más. El contenido, que es lo
  // que no se puede perder, viene del servidor.
  useEffect(() => {
    if (!identidad || !signedIn) {
      return
    }

    // Antes de leer nada: el cache viejo por uuid puede tener notificaciones de
    // otro tenant, de cuando el canal de Pusher era el uuid pelado.
    purgarCacheDeUuidPelado(user?.uuid)

    let vigente = true
    const storageKey = listaStorageKey(identidad)

    const locales = () => {
      try {
        const guardadas = localStorage.getItem(storageKey)
        if (!guardadas) return []
        const parsed = JSON.parse(guardadas)
        if (!Array.isArray(parsed)) return []
        return parsed.map(n => ({ ...n, timestamp: new Date(n.timestamp) }))
      } catch (error) {
        console.error('❌ Error cargando notificaciones de localStorage:', error)
        return []
      }
    }

    const cacheadas = locales()
    setNotifications(cacheadas)
    setHydratedFor(identidad)

    // Las menciones que ya estaban en el navegador antes de salir a buscar. Todo
    // lo que aparezca después es de Pusher y el servidor no lo conocía.
    const keysDelCache = new Set(
      cacheadas.filter(n => n.type === 'mention' && n.key).map(n => n.key)
    )

    apiGetMyMentions()
      .then((respuesta) => {
        if (!vigente) return

        const delServidor = respuesta?.data?.data
        if (!Array.isArray(delServidor)) return

        setNotifications(prev => mergeMenciones(prev, delServidor, t, {
          keysDelCache,
          estado: leerEstado(identidad),
        }))
      })
      .catch((error) => {
        // La campanita no puede romper el login. Si el GET falla, queda como
        // antes de este cambio: sólo lo que llegue por Pusher.
        console.warn('⚠️ No se pudieron traer las menciones:', error?.message || error)
      })

    return () => {
      vigente = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identidad, signedIn])

  // ============================================================================
  // LOCALSTORAGE - Guardar notificaciones cuando cambien
  // ============================================================================
  useEffect(() => {
    if (identidad && hydratedFor === identidad) {
      const storageKey = listaStorageKey(identidad)
      try {
        if (notifications.length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(notifications))

        } else {
          // Si no hay notificaciones, limpiar localStorage
          localStorage.removeItem(storageKey)

        }
        // Momento de corte para la fusión entre pestañas: lo que esta pestaña
        // reciba DESPUÉS de acá es lo único que la otra no puede conocer.
        ultimaEscritura.current = Date.now()
      } catch (error) {
        console.error('❌ Error guardando notificaciones en localStorage:', error)
      }
    }
  }, [notifications, identidad, hydratedFor])

  // ============================================================================
  // OTRAS PESTAÑAS - la campanita es una sola, no una por pestaña
  // ============================================================================
  // El navegador avisa por `storage` a las OTRAS pestañas cada vez que una
  // escribe. Sin esto, cada pestaña persistía la lista entera con la foto que
  // tenía en memoria: leer en una y recibir un aviso en la otra hacía que la
  // segunda reescribiera la lista sin la lectura, y los borrados reaparecían.
  useEffect(() => {
    if (!identidad) return undefined

    const alEscribirOtraPestana = (evento) => {
      if (evento.key !== listaStorageKey(identidad)) return

      let guardadas = []
      try {
        // `newValue` es null cuando la otra pestaña limpió todo: ahí la lista
        // vacía es el dato, no un error.
        const parsed = evento.newValue ? JSON.parse(evento.newValue) : []
        if (Array.isArray(parsed)) {
          guardadas = parsed.map(n => ({ ...n, timestamp: new Date(n.timestamp) }))
        }
      } catch (error) {
        return
      }

      setNotifications(prev => {
        const fusionadas = fusionarEntrePestanas(prev, guardadas, {
          desde: ultimaEscritura.current,
          estado: leerEstado(identidad),
        })

        // Misma referencia si no cambió nada: si no, esta pestaña reescribe, la
        // otra vuelve a fusionar, y quedan rebotando para siempre.
        return mismasNotificaciones(prev, fusionadas) ? prev : fusionadas
      })
    }

    window.addEventListener('storage', alEscribirOtraPestana)

    return () => window.removeEventListener('storage', alEscribirOtraPestana)
  }, [identidad])

  // ============================================================================
  // FUNCIONES DE NOTIFICACIONES
  // ============================================================================

  // ¿El hallazgo del aviso es el que la persona tiene abierto en pantalla?
  //
  // Se lee del ref y no de `location` porque esto lo llaman los listeners de
  // Pusher, que capturaron su render de registro y verían una ruta congelada.
  const estoyViendoElHallazgo = (ticketId) => {
    if (!ticketId) return false
    const { pathname, search } = locationRef.current || {}
    if (!pathname?.includes('/tickets/inbox')) return false
    return new URLSearchParams(search || '').get('ticket') === String(ticketId)
  }

  // Agregar una notificación a la lista.
  //
  // `key` sólo la usan las menciones: es lo que permite aparear la que llega
  // ahora por Pusher con la que va a devolver el servidor en el próximo login,
  // para no mostrarla dos veces ni perder el "ya la vi".
  //
  // `eventId` lo pone el backend y es el mismo para TODAS las entregas del mismo
  // hecho: las que reciben las otras pestañas de la misma persona y las que
  // reenvía un reintento de la cola. Es lo que hace que dos pestañas terminen con
  // UNA sola fila en la campanita en vez de dos, porque el id deja de depender de
  // cuándo llegó a cada una.
  const addNotification = ({ type, title, message, data, key, author, eventId }) => {

    // Además de la lógica de borrado, el id es la key de React de la fila: dos
    // hermanos con la misma key hacen que React reuse el nodo equivocado y el
    // arrastre de una fila aparezca en otra.
    //
    // La `key` de la mención va primero porque es la única identidad que el
    // SERVIDOR también conoce: es lo que aparea el evento en vivo con la fila que
    // devuelve el GET. El `eventId` identifica la entrega, no la mención.
    const id = key ?? eventId ?? `${Date.now()}-${++contadorDeId.current}`

    // Si el usuario ya limpió esta mención, el evento en vivo no la resucita. Pasa
    // con un Pusher demorado o repetido después de un "Limpiar todo", y sin este
    // guard reaparecía en la campanita y disparaba la notificación del navegador,
    // aunque el merge la fuera a descartar en la próxima carga.
    if (key && identidad && leerEstado(identidad)[key]?.dismissed) {
      return
    }

    // Un evento repetido (reconexión de Pusher, reintento de la cola, la copia que
    // ya fusionó otra pestaña) no vuelve a avisar: la notificación ya está en la
    // lista y volver a sonar por lo mismo es exactamente el ruido que hace que la
    // gente apague los avisos.
    //
    // Se decide contra el ref y no adentro del setter: mutar una variable de
    // afuera desde el updater deja el resultado a merced de cuántas veces React
    // decida invocarlo.
    if (yaEstaEnLaLista(notificationsRef.current, id)) {
      return
    }

    const newNotif = {
      id,
      key,
      type,
      title,
      message,
      timestamp: new Date(),
      read: false,
      data,
      author,
    }

    setNotifications(prev => (
      // Segunda barrera, contra dos eventos en el mismo tick: el ref se
      // sincroniza recién en el efecto posterior al render.
      yaEstaEnLaLista(prev, id) ? prev : [newNotif, ...prev]
    ))

    // Un aviso por vez y en el canal que corresponde.
    //
    // Antes salía siempre la notificación del sistema, aunque la persona
    // estuviera con la app adelante: dos avisos por la misma mención. Ahora:
    // pestaña oculta va al sistema, pestaña adelante va al aviso de adentro, y
    // si el hallazgo del que hablan ya está en pantalla no va ninguno, porque lo
    // está leyendo. En los tres casos la notificación entra a la campanita
    // igual, así que nunca se pierde.
    const pestanaVisible = typeof document !== 'undefined' && document.visibilityState === 'visible'

    if (!pestanaVisible) {
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification(title, {
            body: message,
            icon: '/logo192.png',
            badge: '/logo192.png',
            // El `tag` es lo que hace que el navegador REEMPLACE una notificación
            // en vez de apilar otra. Depende de que sea el mismo texto en todas
            // las pestañas: con `id` derivado de la hora de llegada, tres pestañas
            // ocultas daban tres avisos del sistema por el mismo comentario. Con
            // el `event_id` del backend adentro del id, dan uno.
            tag: `notification-${newNotif.id}`,
            data: data
          })
        } catch (error) {
          console.warn('⚠️ No se pudo mostrar notificación del navegador:', error)
        }
      }
      return
    }

    if (!estoyViendoElHallazgo(data?.ticketId)) {
      setToasts(prev => [...prev, newNotif])
    }
  }

  const cerrarToast = useCallback((id) => {
    setToasts(prev => prev.filter(n => n.id !== id))
  }, [])

  // ============================================================================
  // BADGE DE NOTIFICACIONES NO LEÍDAS
  // ============================================================================
  useEffect(() => {
    const unreadCount = notifications.filter(n => !n.read).length

    if ('setAppBadge' in navigator) {
    
      if (unreadCount > 0) {
        navigator.setAppBadge(unreadCount)
          .then(() => console.log(`✅ Badge actualizado a: ${unreadCount}`))
          .catch(err => console.log('⚠️ No se pudo actualizar el badge:', err))
      } else {
        navigator.clearAppBadge()
          .then(() => console.log('✅ Badge limpiado'))
          .catch(err => console.log('⚠️ No se pudo limpiar el badge:', err))
      }
    } else {
      console.log('⚠️ Badging API no soportada en este navegador')
    }
  }, [notifications])

  // ============================================================================
  // ACCIONES DE NOTIFICACIONES
  // ============================================================================

  // Marca menciones en el estado local, que es lo que sobrevive al merge.
  //
  // Los otros 5 tipos no pasan por acá: viven sólo en la lista, y la lista ya se
  // persiste entera en el navegador.
  const marcarMenciones = (lista, cambio) => {
    if (!identidad) {
      return
    }

    const estado = leerEstado(identidad)
    // De la más vieja a la más nueva, porque `tocarEstado` reinserta al final y
    // el recorte de MAX_ESTADO se queda con las últimas. Recorriendo la lista tal
    // como viene (más nueva primero) las nuevas quedaban adelante y eran las
    // primeras en caerse: con más de 200 menciones acumuladas, "Limpiar" perdía
    // justo la marca de las que se acababan de borrar y volvían en el próximo
    // login.
    lista
      .filter(n => n.type === 'mention' && n.key)
      .slice()
      .reverse()
      .forEach(n => tocarEstado(estado, n.key, cambio))

    guardarEstado(identidad, estado)
  }

  // Dos notificaciones son la misma si son el mismo objeto, o si comparten key.
  //
  // Comparar por `id` no alcanza: los 5 tipos que no son menciones usan
  // Date.now() como id, así que dos avisos del mismo milisegundo lo comparten y
  // borrar uno se llevaría al otro sin que se vea. La identidad referencial es
  // exacta para esos, y la key cubre a las menciones, cuyos objetos se recrean
  // en cada merge con el servidor.
  const esLaMisma = (a, b) => a === b || (!!a.key && a.key === b.key)

  // Las notificaciones que la campanita muestra en la MISMA fila que ésta.
  //
  // Usa la clave de `notificationHelpers`, la misma con la que el panel agrupa:
  // si las dos no coinciden, el click marca leído un conjunto distinto del que se
  // ve, el grupo sigue sin leer por las anteriores y el contador baja sin que
  // nada cambie a la vista. Un aviso que no se agrupa es un grupo de uno.
  const delMismoHallazgo = (notif, lista) => {
    const clave = claveDeGrupo(notif)
    if (!clave) {
      return lista.filter(n => esLaMisma(notif, n))
    }
    return lista.filter(n => claveDeGrupo(n) === clave)
  }

  const programarDeshacer = (items) => {
    clearTimeout(timerDeshacer.current)
    setDeshacer({ items })
    timerDeshacer.current = setTimeout(() => setDeshacer(null), MS_DESHACER)
  }

  // Borrado con vuelta atrás.
  //
  // La marca de borrada se escribe YA, no al vencer los seis segundos: si no,
  // cerrar la pestaña en ese rato dejaba la mención sin borrar y volvía en el
  // próximo login, que es justo lo contrario de lo que la persona pidió. El
  // Deshacer levanta la marca y repone el objeto.
  //
  // Un borrado nuevo confirma el anterior en vez de encolarse: dos Deshacer
  // superpuestos no se pueden mostrar en la misma barra, y el que quedaría vivo
  // sería el que la persona ya no está mirando.
  const borrar = (items) => {
    const aBorrar = (items || []).filter(Boolean)
    if (!aBorrar.length) {
      return
    }

    // Las menciones las repone el servidor en la próxima carga, así que sacarlas
    // de la lista no alcanza: sin dejar constancia, volverían enteras al
    // recargar y el botón parecería no hacer nada.
    marcarMenciones(aBorrar, { dismissed: true })

    setNotifications(prev => prev.filter(n => !aBorrar.some(i => esLaMisma(i, n))))
    programarDeshacer(aBorrar)
  }

  const handleDismiss = (notif) => borrar([notif])

  const handleDismissMany = (lista) => borrar(lista)

  const handleUndo = () => {
    if (!deshacer) {
      return
    }

    clearTimeout(timerDeshacer.current)
    marcarMenciones(deshacer.items, { dismissed: false })

    setNotifications(prev => {
      // Contra duplicados: entre el borrado y el Deshacer pudo entrar la misma
      // mención por Pusher, o completarse un merge con el servidor.
      const nuevas = deshacer.items.filter(i => !prev.some(n => esLaMisma(i, n)))
      return [...prev, ...nuevas].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    })

    setDeshacer(null)
  }

  const handleMarkAllRead = () => {
    marcarMenciones(notifications, { read: true })
    setNotifications(prev => prev.map(n => (n.read ? n : { ...n, read: true })))
  }

  // Limpiar todas las notificaciones. Con Deshacer, igual que el borrado de a una:
  // era el único botón del panel y no tenía vuelta atrás.
  const handleClearAll = () => borrar(notifications)

  useEffect(() => () => clearTimeout(timerDeshacer.current), [])

  // Tocar un aviso lo SACA de la campanita, no lo deja marcado como leído.
  //
  // La campanita es la cola de lo que falta ver; el archivo es la vista
  // "Menciones" del inbox, que es justo donde termina este click. Si las leídas
  // se quedaran, con las 20 menciones que devuelve el servidor la lista se
  // convierte en un chorizo donde lo nuevo se pierde entre lo ya visto, y no hay
  // filtro para aislarlo. Es el modelo de la bandeja de Android y del inbox de
  // GitHub: lo que tocaste, se fue.
  //
  // Se va el grupo entero del hallazgo, igual que con la X de la fila: el destino
  // del click es el hallazgo, y ahí quedan todas sus menciones a la vista en el
  // mismo feed. `borrar` deja la barra de Deshacer, que cubre el toque por error.
  const handleNotificationClick = (notif) => {
    const delHallazgo = delMismoHallazgo(notif, notifications)

    borrar(delHallazgo)

    // Si el aviso efímero de esta misma notificación sigue en pantalla, se va con
    // el click: quedaría contando los segundos para avisar de algo que la
    // persona ya abrió.
    setToasts(prev => prev.filter(n => !esLaMisma(notif, n)))

    // Navegar según el tipo de notificación
    if (notif.data?.ticketId) {
      // El `reply` es opcional a propósito: las menciones viejas que ya están en
      // el navegador se guardaron sin él, y sin el parámetro el destino sigue
      // siendo el hallazgo, como antes.
      const ancla = notif.data?.replyId ? `&reply=${notif.data.replyId}` : ''

      // La mención lleva además la lista a "Menciones". El destino de tocarla no
      // es sólo el hallazgo: es el lugar donde están todas las veces que alguien
      // me nombró. Sin esto el hallazgo se abría sobre "Mis tareas", que muchas
      // veces ni siquiera lo contiene, así que la lista de atrás no mostraba
      // seleccionado lo que estaba abierto adelante.
      const vista = notif.type === 'mention' ? '&view=mentioned' : ''

      navigate(`/tickets/inbox?ticket=${notif.data.ticketId}${ancla}${vista}`)
    }
  }

  // ============================================================================
  // PUSHER CHANNELS - Eventos en tiempo real (Laravel Echo)
  // ============================================================================
  useEffect(() => {
    if (!pusherChannel) {

      return
    }

    // Avisa a las vistas que escuchan por su cuenta (hoy la home) que llegó algo,
    // para que refresquen sus datos. Es independiente de la campanita: se dispara
    // aunque el aviso no se muestre por ser del propio usuario.
    const avisarALasVistas = (tipo, data) => {
      window.dispatchEvent(new CustomEvent('ticket-notification-received', {
        detail: { type: tipo, data, timestamp: Date.now() }
      }))
    }

    // ============================================================================
    // TICKET CREADO
    // ============================================================================
    const alCrearse = (data) => {

      const { ticket } = data.payload

      // Prende el punto del ícono de "Mis tareas", que es OTRO indicador que la
      // campanita: lo apaga entrar a la bandeja, no leer el aviso. Va antes del
      // guard del autor a propósito, igual que en la versión anterior: quien crea
      // el hallazgo también lo va a ver en su bandeja.
      dispatch(setHaveNotifications(true))

      if (ticket.owner_id !== user.id) {

        addNotification({
          type: 'created',
          title: `Se te ha creado ${t('ticket_alias')} `,
          message: `${t('ticket_alias')} #${ticket.id}`,
          data: { ticketId: ticket.id },
          eventId: data.event_id,
        })
      } else {
        console.log('⏭️ Ticket creado por el mismo usuario, no notificar')
      }

      avisarALasVistas('ticket-created', data)
    }

    // ============================================================================
    // NUEVO MENSAJE
    // ============================================================================
    const alLlegarUnMensaje = (data) => {

      const { ticket_reply } = data.payload


      if (ticket_reply.user_id !== user.id) {

         const cleanMessage = ticket_reply.message.replace(/<[^>]*>/g, '').trim(); // Limpiar HTML
        addNotification({
          type: 'message',
          title: `${t('new_comment_on_ticket')} ${t('ticket_alias')}  #${ticket_reply.ticket_id}\n${cleanMessage.substring(0, 100)}${cleanMessage.length > 100 ? '...' : ''}`,
          message: cleanMessage,
          data: { ticketId: ticket_reply.ticket_id },
          eventId: data.event_id,
        })
      } else {
        console.log('⏭️ Mensaje enviado por el mismo usuario, no notificar')
      }

      avisarALasVistas('ticket-new-message', data)
    }

    // ============================================================================
    // MENCIÓN (@usuario) EN UN COMENTARIO
    // ============================================================================
    const alSerMencionado = (data) => {

      const { ticket, ticket_reply, mentioned_by, mentioned_by_image } = data.payload || {}

      // Sin ticket no hay deep link ni título posible. Sale antes de tocar
      // ticket.id: si eso tirara TypeError, se cortaría también el
      // dispatchEvent de abajo y no se refrescaría nada, en silencio.
      if (!ticket?.id) {
        return
      }

      // El backend ya saca al autor del fan-out (syncMentions descuenta
      // ticket_reply.user_id), pero el guard queda por simetría con el resto
      // de los listeners: una auto-mención no tiene que sonar.
      if (ticket_reply?.user_id !== user.id) {

        const cleanMessage = (ticket_reply?.message || '').replace(/<[^>]*>/g, '').trim()

        addNotification({
          type: 'mention',
          title: `${mentioned_by ? mentioned_by + ' ' : ''}${t('mentioned_you_in', 'te mencionó en')} ${t('ticket_alias')} #${ticket.id}`,
          message: `${cleanMessage.substring(0, 100)}${cleanMessage.length > 100 ? '...' : ''}`,
          data: { ticketId: ticket.id, replyId: ticket_reply?.id },
          key: ticket_reply?.id ? mentionKey(ticket_reply.id) : undefined,
          author: { name: mentioned_by || '', image: mentioned_by_image || null },
          eventId: data.event_id,
        })
      }

      avisarALasVistas('ticket-mention', data)
    }

    // ============================================================================
    // CAMBIO DE ESTADO
    // ============================================================================
    const alCambiarDeEstado = (data) => {

      const { ticket } = data.payload

      if (data.user.id !== user.id) {

        addNotification({
          type: 'status',
          title: 'Estado de ticket actualizado',
          message: `Ticket #${ticket.id} cambió de estado`,
          data: { ticketId: ticket.id },
          eventId: data.event_id,
        })
      } else {
        console.log('⏭️ Estado cambiado por el mismo usuario, no notificar')
      }

      avisarALasVistas('ticket-change-status', data)
    }

    // ============================================================================
    // TICKET EXPIRADO
    // ============================================================================
    const alExpirar = (data) => {

      const { ticket } = data.payload


      addNotification({
        type: 'expired',
        title: 'Ticket expirado',
        message: `Ticket #${ticket.id} ha expirado`,
        data: { ticketId: ticket.id },
        eventId: data.event_id,
      })

      avisarALasVistas('ticket-expired', data)
    }

    // ============================================================================
    // TICKET ASIGNADO
    // ============================================================================
    const alSerAsignado = (data) => {

      const { ticket } = data.payload

      // Igual que en `.ticket-created`: el punto de "Mis tareas" es otro
      // indicador, con su propio ciclo de vida.
      dispatch(setHaveNotifications(true))

      addNotification({
        type: 'assigned',
        title: 'Se te ha Asignado',
        message: referenciaDelHallazgo(ticket, t('ticket_alias')),
        data: { ticketId: ticket.id },
        eventId: data.event_id,
      })

      avisarALasVistas('ticket-assigned', data)
    }

    const manejadores = {
      '.ticket-created': alCrearse,
      '.ticket-new-message': alLlegarUnMensaje,
      '.ticket-mention': alSerMencionado,
      '.ticket-change-status': alCambiarDeEstado,
      '.ticket-expired': alExpirar,
      '.ticket-assigned': alSerAsignado,
    }

    Object.entries(manejadores).forEach(([evento, manejador]) => {
      pusherChannel.listen(evento, manejador)
    })

    // Echo NO limpia esto solo: el canal es el mismo objeto entre renders, así que
    // cada vez que el efecto vuelve a correr (cambio de usuario sin recargar)
    // quedaban DOS handlers del mismo evento sobre el mismo canal, y con ellos dos
    // avisos por hecho. Se pasa el manejador y no sólo el nombre del evento para
    // no desregistrar de paso a otro consumidor del mismo canal.
    return () => {
      Object.entries(manejadores).forEach(([evento, manejador]) => {
        pusherChannel.stopListening(evento, manejador)
      })
    }
  }, [pusherChannel, user?.id])

  // ============================================================================
  // LOG DE ESTADO DE NOTIFICACIONES
  // ============================================================================


  // ============================================================================
  // CONTEXTO
  // ============================================================================
  const contextValue = {
    notifications,
    clearAll: handleClearAll,
    handleNotificationClick,
    dismissOne: handleDismiss,
    dismissMany: handleDismissMany,
    markAllRead: handleMarkAllRead,
    undo: handleUndo,
    // Sólo la cantidad: el texto lo arma el panel, que es el que tiene el idioma
    // y el que sabe si fue una o fueron todas.
    pendingUndo: deshacer ? deshacer.items.length : 0,
  }

  // Solo proveer contexto si el usuario está autenticado Y la lista ya es la suya.
  //
  // El `hydratedFor` no es cosmético: al cambiar de sesión, el primer render con
  // el usuario nuevo todavía tiene en el estado las notificaciones del anterior, y
  // el efecto que carga su cache corre después. Sin este guard, la campanita
  // alcanzaba a mostrar títulos y extractos de hallazgos de otro usuario, que en
  // otro tenant puede ser de otra empresa. Los consumidores usan el valor por
  // defecto del contexto, así que ven una campanita vacía por un frame.
  if (!signedIn || hydratedFor !== identidad) {
    return children || null
  }

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationToaster
        toasts={toasts}
        onClose={cerrarToast}
        onOpen={handleNotificationClick}
      />
      <NotificationUndoBar
        count={contextValue.pendingUndo}
        onUndo={handleUndo}
      />
    </NotificationContext.Provider>
  )
}

export default NotificationManager